import React, {useEffect, useState} from "react";
import {Alert, Container, Spinner} from "react-bootstrap";
import ZweckKasten from "../../Components/EntgangenerUmsatz/ZweckKasten.jsx";
import ZeitraumWahl from "../../Components/EntgangenerUmsatz/ZeitraumWahl.jsx";
import SchaetzungHinweis from "../../Components/EntgangenerUmsatz/SchaetzungHinweis.jsx";
import PreisgrundlageSchalter from "../../Components/EntgangenerUmsatz/PreisgrundlageSchalter.jsx";
import EntgangenerUmsatzKacheln from "../../Components/EntgangenerUmsatz/EntgangenerUmsatzKacheln.jsx";
import EntgangenerUmsatzTabelle from "../../Components/EntgangenerUmsatz/EntgangenerUmsatzTabelle.jsx";
import useStatisticsData from "../../Components/Statistics/useStatisticsData.js";
import {useQueryState} from "../../Components/SalesAnalysis/useQueryState.js";
import {berechnePresetZeitraum} from "../../Components/EntgangenerUmsatz/format.js";

const LAUFENDES_JAHR = berechnePresetZeitraum("laufendes_jahr");

/**
 * Eigenständige Seite "Entgangener Umsatz" (Freigabe Thorsten 22.09.2026,
 * docs/tasks/2026-09-18-cosmos-bereich/UMSETZUNG.md Abschnitt G). Zeitraum
 * und Preisgrundlage liegen in der URL bzw. serverseitig (siehe
 * PreisgrundlageSchalter), damit ein geteilter Link denselben Stand zeigt.
 */
export default function EntgangenerUmsatzIndex() {
    const [preset, setPreset] = useQueryState("zeitraum", "laufendes_jahr");
    const [von, setVon] = useQueryState("von", LAUFENDES_JAHR.von);
    const [bis, setBis] = useQueryState("bis", LAUFENDES_JAHR.bis);
    const [preisgrundlageOverride, setPreisgrundlageOverride] = useState(null);

    const wechslePreset = (neuerPreset) => {
        setPreset(neuerPreset);
        if (neuerPreset !== "frei") {
            const zeitraum = berechnePresetZeitraum(neuerPreset);
            setVon(zeitraum.von);
            setBis(zeitraum.bis);
        }
    };

    const setzeVon = (wert) => {
        setPreset("frei");
        setVon(wert);
    };

    const setzeBis = (wert) => {
        setPreset("frei");
        setBis(wert);
    };

    const {data, loading, error} = useStatisticsData("/cosmos/daten/entgangener-umsatz", {von, bis});

    // Nach einer Umstellung der Preisgrundlage sofort neu anzeigen, ohne auf
    // den nächsten Von/Bis-Wechsel zu warten - der Server liefert die neue
    // Preisgrundlage in der Antwort des PUT-Aufrufs zurück.
    useEffect(() => {
        if (data?.preisgrundlage) {
            setPreisgrundlageOverride(null);
        }
    }, [data?.preisgrundlage]);

    const aktivePreisgrundlage = preisgrundlageOverride ?? data?.preisgrundlage ?? "brutto";

    return (
        <Container fluid className="pt-2 pb-4">
            <ZweckKasten />
            <h1 className="h4 mb-2">Entgangener Umsatz</h1>

            <SchaetzungHinweis preisgrundlage={aktivePreisgrundlage} belastbarkeit={data?.belastbarkeit} />

            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                <ZeitraumWahl
                    preset={preset}
                    onPresetChange={wechslePreset}
                    von={von}
                    bis={bis}
                    onVonChange={setzeVon}
                    onBisChange={setzeBis}
                    min={data?.grenzen?.min}
                    max={data?.grenzen?.max}
                />
                <PreisgrundlageSchalter preisgrundlage={aktivePreisgrundlage} onChanged={setPreisgrundlageOverride} />
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            {loading && !data && (
                <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Wird geladen…</span>
                    </Spinner>
                </div>
            )}

            {data && (
                <>
                    <EntgangenerUmsatzKacheln
                        summe={data.summe}
                        preisgrundlage={aktivePreisgrundlage}
                        farbtoneAnzahl={data.zeilen?.length ?? 0}
                        artikelOhnePreis={data.artikel_ohne_preis ?? 0}
                    />
                    <EntgangenerUmsatzTabelle
                        zeilenFarbton={data.zeilen}
                        zeilenProduktlinie={data.je_produktlinie}
                        summe={data.summe}
                    />
                </>
            )}
        </Container>
    );
}
